import type { ActivityRow } from '../dashboardShared/types';
import type { AdminJob, VendorApprovalPending } from './useAdminHomeData';

export const relativeTime = (iso: string | number | null | undefined, now: number = Date.now()): string => {
  if (!iso) return 'never';
  const then = typeof iso === 'number' ? iso : new Date(iso).getTime();
  if (Number.isNaN(then) || then === 0) return 'never';
  const minutes = Math.floor((now - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const toMs = (value: string | number | null | undefined): number => {
  if (!value) return 0;
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/** The moment a job row is labelled with, so the feed sorts by what it shows. */
export const jobActivityTime = (job: AdminJob): number => toMs(job.completedAt || job.startedAt || job.createdAt);

const JOBS_HREF = '/app/admin/jobs';
const VENDOR_APPROVALS_HREF = '/app/admin/brands-vendors/approvals';

/**
 * Recent platform activity: pending vendor registrations and ingestion jobs merged into one
 * feed, newest first. A completed run that added nothing is a no-op and is left out; a run
 * still in flight is shown as running rather than as "0 new products".
 */
export function deriveAdminActivity(
  pendingVendors: VendorApprovalPending[],
  recentJobs: AdminJob[],
  now: number = Date.now(),
  limit = 6
): ActivityRow[] {
  const entries: Array<{ at: number; row: ActivityRow }> = [];

  for (const vendor of pendingVendors) {
    entries.push({
      at: toMs(vendor.submittedAt),
      row: {
        id: `vendor-${vendor.id}`,
        tag: 'Vendor',
        text: `${vendor.vendorName} submitted a registration request`,
        when: relativeTime(vendor.submittedAt, now),
        href: VENDOR_APPROVALS_HREF,
      },
    });
  }

  for (const job of recentJobs) {
    const source = job.brandName || 'a source';
    const at = jobActivityTime(job);
    const when = relativeTime(job.completedAt || job.startedAt || job.createdAt, now);
    if (job.status === 'failed') {
      const reason = job.result?.errors?.[0];
      entries.push({
        at,
        row: {
          id: `job-${job.id}`,
          tag: 'Job',
          text: `${job.brandName || 'Ingestion'} job failed${reason ? `: ${reason}` : ''}`,
          when,
          href: JOBS_HREF,
        },
      });
    } else if (job.status === 'completed') {
      const created = job.result?.productsCreated ?? 0;
      if (created <= 0) continue;
      entries.push({
        at,
        row: {
          id: `job-${job.id}`,
          tag: 'Catalog',
          text: `${created.toLocaleString()} new product${created === 1 ? '' : 's'} added from ${source}`,
          when,
          href: JOBS_HREF,
        },
      });
    } else if (job.status === 'running' || job.status === 'pending' || job.status === 'retrying') {
      entries.push({
        at,
        row: {
          id: `job-${job.id}`,
          tag: 'Job',
          text: `Ingestion from ${source} is ${job.status === 'pending' ? 'queued' : 'running'}`,
          when,
          href: JOBS_HREF,
        },
      });
    }
  }

  return entries
    .sort((a, b) => b.at - a.at)
    .slice(0, limit)
    .map((e) => e.row);
}

/** Subtitle for the failed-jobs tile: names the source when every failure shares one. */
export function failedJobsSourceNote(failedJobs: AdminJob[]): string {
  if (failedJobs.length === 0) return 'No failures';
  const sources = new Set(failedJobs.map((j) => j.brandName || 'Unknown source'));
  if (sources.size === 1) return `All from ${Array.from(sources)[0]}`;
  return `${sources.size} sources`;
}
